import { useState, useEffect, useRef } from 'react'
import Modal from '@/components/layout/Modal'
import type { PasswordMode, UserData } from '@/types'
import { weightUnit, kgToDisplay, displayToKg, detectRegion, setRegion as persistRegion, type Region } from '@/utils/time'
import { REGION_DATA } from '@/components/layout/HealthDropdown'
import { notificationsSupported, notificationsEnabled, getNotificationPermission, requestNotificationPermission, enableNotifications, disableNotifications } from '@/utils/notifications'
import { listBackups, getBackup, setPassword as apiSetPassword, removePassword as apiRemovePassword, updateSettings as apiUpdateSettings } from '@/utils/api'
import type { BackupInfo } from '@/types'

const ALL_REGIONS: Region[] = ['AU', 'UK', 'US', 'NZ']

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  section?: string
  weight: number
  gender: 'male' | 'female'
  timeoutMinutes?: number
  autoLogoutMinutes: number
  passwordHash?: string
  passwordSalt?: string
  passwordMode?: PasswordMode
  backupEmail?: string
  onSave: (settings: {
    weight: number
    gender: 'male' | 'female'
    timeoutMinutes?: number
    autoLogoutMinutes: number
    passwordHash?: string
    passwordSalt?: string
    passwordMode?: PasswordMode
    backupEmail?: string
    rawPassword?: string
  }) => void
  userId: string
  getCachedPassword?: () => string | null
  onDownloadData?: () => void
  onImportData?: (imported: Partial<UserData>) => void
  onDeleteData?: () => void
}

export default function SettingsModal({
  isOpen,
  onClose,
  section,
  weight: initialWeight,
  gender: initialGender,
  timeoutMinutes: initialTimeout,
  autoLogoutMinutes: initialAutoLogout,
  passwordHash: initialPasswordHash,
  passwordSalt: initialPasswordSalt,
  passwordMode: initialPasswordMode,
  backupEmail: initialBackupEmail,
  onSave,
  userId,
  getCachedPassword,
  onDownloadData,
  onImportData,
  onDeleteData,
}: SettingsModalProps) {
  // Basic settings
  const [weight, setWeight] = useState(kgToDisplay(initialWeight).toString())
  const [gender, setGender] = useState<'male' | 'female'>(initialGender)
  const [copied, setCopied] = useState(false)
  const [autoLogoutMinutes, setAutoLogoutMinutes] = useState(initialAutoLogout.toString())

  // Password settings
  const [showPasswordSection, setShowPasswordSection] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordMode, setPasswordMode] = useState<PasswordMode>(initialPasswordMode || 'on_load')
  // initialTimeout is undefined for accounts that have never set a lock
  // timeout — the dropdown displays "5" as a default in that case but the
  // value isn't sent through the save path unless the user changes it.
  const [timeoutMinutes, setTimeoutMinutes] = useState((initialTimeout ?? 5).toString())
  const [passwordError, setPasswordError] = useState('')
  const [passwordSaving, setPasswordSaving] = useState(false)

  // Backup email
  const [backupEmail, setBackupEmail] = useState(initialBackupEmail || '')
  const [isEditingEmail, setIsEditingEmail] = useState(false)

  // Import state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importPreview, setImportPreview] = useState<{ data: Partial<UserData>; entryCount: number; drinkCount: number } | null>(null)
  const [importError, setImportError] = useState('')

  // Region
  const [region, setRegion] = useState<Region>(detectRegion)
  // Notifications
  const [notifEnabled, setNotifEnabled] = useState(notificationsEnabled)
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false)
  const regionRef = useRef<HTMLDivElement>(null)

  // Backups
  const [backups, setBackups] = useState<BackupInfo[]>([])
  const [backupsLoading, setBackupsLoading] = useState(false)
  const [backupsLoaded, setBackupsLoaded] = useState(false)
  const [restoreConfirm, setRestoreConfirm] = useState<string | null>(null)
  const [restoring, setRestoring] = useState(false)

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  const hasPassword = !!initialPasswordHash

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      setWeight(kgToDisplay(initialWeight).toString())
      setGender(initialGender)
      setTimeoutMinutes((initialTimeout ?? 5).toString())
      setAutoLogoutMinutes(initialAutoLogout.toString())
      setPasswordMode(initialPasswordMode || 'on_load')
      setBackupEmail(initialBackupEmail || '')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordError('')
      setShowPasswordSection(false)
      setIsEditingEmail(false)
      setShowDeleteConfirm(false)
      setDeleteConfirmText('')
      setImportPreview(null)
      setImportError('')
      setSubmitError('')
      setSubmitting(false)
      setRegionDropdownOpen(false)
      setBackups([])
      setBackupsLoaded(false)
      setRestoreConfirm(null)
      setRestoring(false)
    }
  }, [isOpen, initialWeight, initialGender, initialTimeout, initialAutoLogout, initialPasswordMode, initialBackupEmail])

  // Load backups when section is shown
  useEffect(() => {
    if (!isOpen || section !== 'backups' || backupsLoaded) return
    setBackupsLoading(true)
    listBackups(userId).then(result => {
      setBackups(result.backups || [])
      setBackupsLoaded(true)
    }).catch(() => {
      setBackups([])
      setBackupsLoaded(true)
    }).finally(() => setBackupsLoading(false))
  }, [isOpen, section, backupsLoaded, userId])

  // Close region dropdown on click outside
  useEffect(() => {
    if (!regionDropdownOpen) return
    const handleMouseDown = (e: MouseEvent) => {
      if (regionRef.current && !regionRef.current.contains(e.target as Node)) {
        setRegionDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [regionDropdownOpen])

  const [submitError, setSubmitError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setSubmitError('')
    setSubmitting(true)

    const weightDisplay = parseFloat(weight)
    if (isNaN(weightDisplay) || weightDisplay <= 0) {
      setSubmitting(false)
      return
    }
    const weightKg = displayToKg(weightDisplay)

    const autoLogoutNum = parseInt(autoLogoutMinutes) || 0
    // Normalize email to match what the server stores (it lowercases
    // before persisting). Sending the normalized form through both
    // PATCH and the subsequent PUT keeps client data state in sync
    // with the server.
    const newEmail = backupEmail.trim().toLowerCase() || undefined
    const initialEmailNormalized = initialBackupEmail?.toLowerCase()

    // The data PUT endpoint refuses to mutate backupEmail (H1 fix).
    // If the user changed it, route the change through PATCH /settings.
    // Always send the cached password if we have one — the server is
    // the source of truth on whether it's required. (We can't trust the
    // local `hasPassword` prop because the user may have just set a
    // password in this same modal session, making it stale.)
    if (newEmail !== initialEmailNormalized) {
      const currentPassword = getCachedPassword?.() ?? undefined
      const result = await apiUpdateSettings(userId, {
        backupEmail: newEmail || '',
        currentPassword,
      })
      if (!result.success) {
        if (result.error === 'Password required to change settings') {
          setSubmitError(
            'Lock the app and re-enter your password, then change the backup email — your password isn\'t cached in this session.',
          )
        } else {
          setSubmitError(result.error || 'Failed to update backup email.')
        }
        setSubmitting(false)
        return
      }
    }

    // SECURITY: do NOT carry password-section form state through the data
    // PUT. The handleSavePassword button is the only path that mutates
    // passwordHash / passwordSalt / passwordMode / timeoutMinutes on the
    // server; this form just edits weight / gender / autoLogoutMinutes
    // (and backupEmail, handled above via PATCH /settings). Sending the
    // form's `passwordMode` or `timeoutMinutes` state here causes a 403
    // when it diverges from what's stored (the form state may not have
    // re-synced after the user set their password earlier).
    onSave({
      weight: weightKg,
      gender,
      timeoutMinutes: initialTimeout,
      autoLogoutMinutes: autoLogoutNum,
      passwordHash: initialPasswordHash,
      passwordSalt: initialPasswordSalt,
      passwordMode: initialPasswordMode,
      // Use the (normalized) new email if it just changed — that's now
      // what the server stores. Otherwise echo the initial value so the
      // PUT is a no-op for this field.
      backupEmail: newEmail !== initialEmailNormalized ? newEmail : initialBackupEmail,
    })
    onClose()
  }

  const handleCopyUrl = async () => {
    const url = `${window.location.origin}/u/${userId}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const input = document.createElement('input')
      input.value = url
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const verifyCurrentPassword = async (password: string): Promise<boolean> => {
    if (!initialPasswordHash) return false
    try {
      const response = await fetch(`/api/user/${encodeURIComponent(userId)}/password/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      })
      const result = await response.json()
      return result.valid === true
    } catch {
      return false
    }
  }

  const handleSavePassword = async () => {
    setPasswordError('')

    // Validation
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    // If changing password, verify current
    if (hasPassword) {
      if (!currentPassword) {
        setPasswordError('Current password is required')
        return
      }
      const isValid = await verifyCurrentPassword(currentPassword)
      if (!isValid) {
        setPasswordError('Current password is incorrect')
        return
      }
    }

    setPasswordSaving(true)

    try {
      const timeoutNum = parseInt(timeoutMinutes) || 5
      const autoLogoutNum = parseInt(autoLogoutMinutes) || 0
      const effectiveTimeout = passwordMode === 'after_timeout' ? timeoutNum : 0

      // SECURITY: write the new password via the dedicated endpoint so the
      // server hashes it and verifies the current password. We never send
      // a client-computed hash via the data PUT — that would let anyone
      // who knows the URL take over the account without the current
      // password.
      const result = await apiSetPassword(
        userId,
        newPassword,
        hasPassword ? currentPassword : undefined,
        passwordMode,
        effectiveTimeout,
      )
      if (!result.success) {
        setPasswordError(result.error || 'Failed to update password')
        return
      }

      // Tell useUser (via onSave) about the new raw password so it can
      // re-derive the AES-GCM key and re-encrypt data on the next save.
      // passwordHash/passwordSalt are sent as 'REDACTED' so the PUT
      // preserves the values the server already wrote in apiSetPassword.
      onSave({
        weight: displayToKg(parseFloat(weight)) || initialWeight,
        gender,
        timeoutMinutes: effectiveTimeout,
        autoLogoutMinutes: autoLogoutNum,
        passwordHash: 'REDACTED',
        passwordSalt: 'REDACTED',
        passwordMode,
        backupEmail: backupEmail.trim() || undefined,
        rawPassword: newPassword,
      })

      // Mark session as unlocked
      sessionStorage.setItem(`ssdl:unlocked:${userId}`, 'true')

      setShowPasswordSection(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } finally {
      setPasswordSaving(false)
    }
  }

  const handleRemovePassword = async () => {
    if (!currentPassword) {
      setPasswordError('Current password is required')
      return
    }

    // SECURITY: delete the password via the dedicated endpoint so the
    // server verifies the current password before clearing the hash.
    const result = await apiRemovePassword(userId, currentPassword)
    if (!result.success) {
      setPasswordError(result.error || 'Current password is incorrect')
      return
    }

    onSave({
      weight: displayToKg(parseFloat(weight)) || initialWeight,
      gender,
      timeoutMinutes: 0,
      autoLogoutMinutes: parseInt(autoLogoutMinutes) || 0,
      passwordHash: undefined,
      passwordSalt: undefined,
      passwordMode: undefined,
      backupEmail: backupEmail.trim() || undefined,
    })

    sessionStorage.removeItem(`ssdl:unlocked:${userId}`)
    setShowPasswordSection(false)
    setCurrentPassword('')
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset file input so the same file can be re-selected
    e.target.value = ''

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string)

        // Validate structure
        if (!parsed || typeof parsed !== 'object') {
          setImportError('Invalid file format')
          return
        }

        if (!Array.isArray(parsed.entries)) {
          setImportError('Invalid file: missing entries array')
          return
        }

        // Validate entries have required fields
        for (const entry of parsed.entries) {
          if (!entry.finished_at || !entry.drink) {
            setImportError('Invalid file: entries missing required fields (finished_at, drink)')
            return
          }
          // Non-gap drinks must have numeric volume and alcohol — a non-numeric
          // value (e.g. "N/A") would compute to NaN and corrupt every later BAC.
          if (!entry.is_gap) {
            const numeric = (v: unknown) => Number.isFinite(Number(v))
            if (!numeric(entry.drink.milliliters) || !numeric(entry.drink.alcohol)) {
              setImportError('Invalid file: a drink has a non-numeric volume or alcohol value')
              return
            }
          }
        }

        setImportError('')
        setImportPreview({
          data: parsed,
          entryCount: parsed.entries.length,
          drinkCount: parsed.drinks?.length || 0,
        })
      } catch {
        setImportError('Could not parse file. Please select a valid JSON export.')
      }
    }
    reader.readAsText(file)
  }

  const maskEmail = (email: string) => {
    const [local, domain] = email.split('@')
    if (!domain) return email
    const maskedLocal = local.slice(0, 2) + '***'
    const [domainName, tld] = domain.split('.')
    const maskedDomain = domainName.slice(0, 2) + '***.' + tld
    return maskedLocal + '@' + maskedDomain
  }

  const sectionTitles: Record<string, string> = {
    'about-you': 'About You',
    'password': 'Password',
    'backup-email': 'Backup Email',
    'backups': 'Backups',
    'auto-logout': 'Auto Logout',
    'data': 'Data Management',
  }

  const title = section ? sectionTitles[section] || 'Settings' : 'Settings'
  const showSection = (s: string) => !section || section === s

  const handleRegionChange = (r: Region) => {
    setRegion(r)
    persistRegion(r)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="settings-form"
            className="btn btn-primary disabled:opacity-60 disabled:cursor-wait inline-flex items-center gap-2"
            disabled={submitting}
          >
            {submitting && (
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth={3} opacity="0.3" />
                <path strokeLinecap="round" strokeWidth={3} d="M22 12a10 10 0 00-10-10" />
              </svg>
            )}
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="settings-form" onSubmit={handleSubmit} className="space-y-6">
        <fieldset disabled={submitting} className={submitting ? 'opacity-60 pointer-events-none' : ''}>
        {/* Weight */}
        {showSection('about-you') && (<>
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
            Body Weight ({weightUnit()})
          </label>
          <input
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="80"
            className="w-full"
            min="30"
            max="300"
            step="0.5"
            required
          />
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            Used to calculate your Blood Alcohol Content (BAC)
          </p>
        </div>

        {/* Gender */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
            Gender
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="gender"
                value="male"
                checked={gender === 'male'}
                onChange={() => setGender('male')}
                className="w-4 h-4 accent-[var(--accent-teal)]"
              />
              <span className="text-[var(--text-primary)]">Male</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="gender"
                value="female"
                checked={gender === 'female'}
                onChange={() => setGender('female')}
                className="w-4 h-4 accent-[var(--accent-teal)]"
              />
              <span className="text-[var(--text-primary)]">Female</span>
            </label>
          </div>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            Affects BAC calculation (different body water content)
          </p>
        </div>

        {/* Region */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
            Country / Region
          </label>
          <div className="relative" ref={regionRef}>
            <button
              type="button"
              onClick={() => setRegionDropdownOpen(prev => !prev)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-[var(--border)] bg-[var(--bg-card)] hover:bg-[var(--bg-card)]/80 transition-colors cursor-pointer text-left"
            >
              <span className="flex items-center gap-2">
                <span className="text-lg">{REGION_DATA[region].flag}</span>
                <span className="text-sm text-[var(--text-primary)]">{REGION_DATA[region].label}</span>
              </span>
              <svg className={`w-4 h-4 text-[var(--text-secondary)] transition-transform ${regionDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {regionDropdownOpen && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-[var(--bg-secondary)] border border-[var(--border)] rounded-lg shadow-lg z-50 overflow-hidden">
                {ALL_REGIONS.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => { handleRegionChange(r); setRegionDropdownOpen(false) }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left cursor-pointer transition-colors ${
                      r === region
                        ? 'bg-[var(--accent-teal)]/10 text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <span className="text-lg">{REGION_DATA[r].flag}</span>
                    <span className="text-sm">{REGION_DATA[r].label}</span>
                    {r === region && (
                      <svg className="w-4 h-4 text-[var(--accent-teal)] ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            Affects units ({region === 'US' ? 'lbs, fl oz' : 'kg, ml'}), date format, health guidelines, and crisis helplines
          </p>
        </div>

        {/* Notifications */}
        {notificationsSupported() && (
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
            Milestone Notifications
          </label>
          <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-card)]">
            <div>
              <p className="text-sm text-[var(--text-primary)]">Browser notifications</p>
              <p className="text-xs text-[var(--text-secondary)]">Get notified when you hit sobriety milestones</p>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (notificationsEnabled()) {
                  disableNotifications()
                  setNotifEnabled(false)
                } else {
                  const perm = getNotificationPermission()
                  if (perm === 'granted') {
                    enableNotifications()
                    setNotifEnabled(true)
                  } else if (perm === 'default') {
                    const granted = await requestNotificationPermission()
                    setNotifEnabled(granted)
                  }
                  // If 'denied', browser blocked it — can't do anything
                }
              }}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                notifEnabled ? 'bg-[var(--accent-teal)]' : 'bg-[var(--border)]'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                notifEnabled ? 'translate-x-5' : ''
              }`} />
            </button>
          </div>
          {getNotificationPermission() === 'denied' && (
            <p className="mt-1 text-xs text-[var(--bac-danger)]">
              Notifications are blocked by your browser. Enable them in your browser settings for this site.
            </p>
          )}
        </div>
        )}

        {/* Your URL */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
            Your Unique URL
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={`${window.location.origin}/u/${userId}`}
              readOnly
              className="flex-1 bg-[var(--bg-card)] text-sm font-mono"
            />
            <button
              type="button"
              onClick={handleCopyUrl}
              className="btn btn-secondary text-sm"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            Bookmark this URL to access your drink log from any device
          </p>
        </div>
        </>)}

        {/* Password Protection */}
        {showSection('password') && (<>
        <div>
          <h3 className="text-sm font-medium text-[var(--text-secondary)] mb-3">
            Password Protection
          </h3>

          {!showPasswordSection ? (
            <>
              {hasPassword ? (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="text-green-400 font-medium">Password enabled — End-to-end encrypted</span>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {passwordMode === 'after_timeout'
                      ? `Lock after ${timeoutMinutes} minutes of inactivity`
                      : 'Lock on every visit'}
                    {' · '}Your data is encrypted before it leaves your device
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-red-400 font-medium">No password set — Not encrypted</span>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)]">
                    Anyone with this URL can view your drink log. Set a password to enable end-to-end encryption.
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowPasswordSection(true)}
                className="text-sm text-[var(--accent-teal)] hover:underline"
              >
                {hasPassword ? 'Change or remove password' : 'Set up password'}
              </button>
            </>
          ) : (
            <div className="space-y-4">
              {!hasPassword ? (
                <div className="p-3 bg-[var(--accent-teal)]/10 border border-[var(--accent-teal)]/30 rounded-lg">
                  <p className="text-sm text-[var(--text-secondary)]">
                    Setting a password enables <strong className="text-[var(--text-primary)]">end-to-end encryption</strong>. Your data will be encrypted on your device before it reaches our servers.
                  </p>
                  <p className="text-sm text-[var(--accent-orange)] mt-2">
                    If you forget your password, your encrypted data cannot be recovered — not even by us.
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-[var(--accent-orange)]/10 border border-[var(--accent-orange)]/30 rounded-lg">
                  <p className="text-sm text-[var(--accent-orange)]">
                    Changing your password will re-encrypt your data with the new password.
                  </p>
                </div>
              )}

              {hasPassword && (
                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-1">
                  {hasPassword ? 'New Password' : 'Password'}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="w-full"
                />
              </div>

              {/* Password Mode */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">
                  When to require password?
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-card)] cursor-pointer hover:bg-[var(--bg-card)]/80 transition-colors">
                    <input
                      type="radio"
                      name="passwordMode"
                      checked={passwordMode === 'on_load'}
                      onChange={() => setPasswordMode('on_load')}
                      className="w-4 h-4 accent-[var(--accent-teal)]"
                    />
                    <div>
                      <p className="text-[var(--text-primary)] font-medium">Every visit</p>
                      <p className="text-xs text-[var(--text-secondary)]">Ask for password each time you open the page</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-card)] cursor-pointer hover:bg-[var(--bg-card)]/80 transition-colors">
                    <input
                      type="radio"
                      name="passwordMode"
                      checked={passwordMode === 'after_timeout'}
                      onChange={() => setPasswordMode('after_timeout')}
                      className="w-4 h-4 accent-[var(--accent-teal)]"
                    />
                    <div>
                      <p className="text-[var(--text-primary)] font-medium">After inactivity</p>
                      <p className="text-xs text-[var(--text-secondary)]">Lock after a period of no activity</p>
                    </div>
                  </label>
                </div>
              </div>

              {passwordMode === 'after_timeout' && (
                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-1">
                    Lock after (minutes)
                  </label>
                  <select
                    value={timeoutMinutes}
                    onChange={(e) => setTimeoutMinutes(e.target.value)}
                    className="w-full bg-[var(--bg-card)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)]"
                  >
                    <option value="1">1 minute</option>
                    <option value="2">2 minutes</option>
                    <option value="5">5 minutes</option>
                    <option value="10">10 minutes</option>
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                  </select>
                </div>
              )}

              {passwordError && (
                <p className="text-sm text-[var(--bac-danger)]">{passwordError}</p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSavePassword}
                  disabled={passwordSaving}
                  className="btn btn-primary flex-1 disabled:opacity-50"
                >
                  {passwordSaving ? 'Saving...' : (hasPassword ? 'Update Password' : 'Set Password')}
                </button>
                {hasPassword && (
                  <button
                    type="button"
                    onClick={handleRemovePassword}
                    className="btn btn-secondary text-red-400"
                  >
                    Remove
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowPasswordSection(false)
                  setPasswordError('')
                  setCurrentPassword('')
                  setNewPassword('')
                  setConfirmPassword('')
                }}
                className="text-sm text-[var(--text-secondary)] hover:underline"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        </>)}

        {/* Backup Email — own top-level section so users can find it directly
            from the Settings dropdown instead of digging through Password */}
        {showSection('backup-email') && (
        <div>
          <h3 className="text-sm font-medium text-[var(--text-secondary)] mb-3">
            Backup Email (Optional)
          </h3>

          {initialBackupEmail && !isEditingEmail ? (
            <>
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-green-400 font-medium">Recovery email saved</span>
                </div>
                <p className="text-xs text-[var(--text-secondary)]">
                  {maskEmail(initialBackupEmail)}
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setIsEditingEmail(true)}
                  className="text-sm text-[var(--accent-teal)] hover:underline"
                >
                  Change email
                </button>
                <button
                  type="button"
                  onClick={() => setBackupEmail('')}
                  className="text-sm text-red-400 hover:underline"
                >
                  Remove email
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="p-3 mb-3 bg-[var(--accent-orange)]/10 border border-[var(--accent-orange)]/30 rounded-lg">
                <p className="text-sm text-[var(--accent-orange)]">
                  Adding an email removes full anonymity. We'll only use it to help you recover your URL if you lose it.
                </p>
              </div>
              <input
                type="email"
                value={backupEmail}
                onChange={(e) => setBackupEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full"
              />
            </>
          )}
        </div>
        )}

        {/* Backups */}
        {showSection('backups') && (
        <div>
          <h3 className="text-sm font-medium text-[var(--text-secondary)] mb-3">
            Data Backups
          </h3>
          <p className="text-xs text-[var(--text-secondary)] mb-3">
            The last 3 versions of your data are saved automatically every time you log a drink.
          </p>
          {backupsLoading ? (
            <p className="text-sm text-[var(--text-secondary)]">Loading backups...</p>
          ) : backups.length === 0 ? (
            <p className="text-sm text-[var(--text-secondary)]">No backups available yet.</p>
          ) : (
            <div className="space-y-2">
              {backups.map(backup => (
                <div key={backup.timestamp} className="flex items-center justify-between p-3 bg-[var(--bg-card)] rounded-lg">
                  <div>
                    <p className="text-sm text-[var(--text-primary)]">{backup.date}</p>
                    <p className="text-xs text-[var(--text-secondary)]">{new Date(parseInt(backup.timestamp)).toLocaleString()}</p>
                  </div>
                  {restoreConfirm === backup.timestamp ? (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setRestoreConfirm(null)}
                        className="btn btn-secondary text-xs py-1 px-2"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={restoring}
                        onClick={async () => {
                          setRestoring(true)
                          try {
                            const result = await getBackup(userId, backup.timestamp)
                            if (result.data?.value && onImportData) {
                              onImportData(result.data.value)
                              onClose()
                            }
                          } catch { /* ignore */ }
                          setRestoring(false)
                        }}
                        className="btn text-xs py-1 px-2 bg-[var(--accent-orange)] text-white hover:bg-[var(--accent-orange)]/80"
                      >
                        {restoring ? 'Restoring...' : 'Confirm'}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRestoreConfirm(backup.timestamp)}
                      className="text-sm text-[var(--accent-teal)] hover:underline cursor-pointer"
                    >
                      Restore
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Auto Logout */}
        {showSection('auto-logout') && (
        <div>
          <h3 className="text-sm font-medium text-[var(--text-secondary)] mb-3">
            Auto Logout
          </h3>
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-1">
              Log out after inactivity (minutes)
            </label>
            <select
              value={autoLogoutMinutes}
              onChange={(e) => setAutoLogoutMinutes(e.target.value)}
              className="w-full bg-[var(--bg-card)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)]"
            >
              <option value="0">Disabled</option>
              <option value="5">5 minutes</option>
              <option value="10">10 minutes</option>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
            </select>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              Automatically log out and clear local data after this period of inactivity. This is more secure than password lock.
            </p>
          </div>
        </div>
        )}

        {/* Data Management */}
        {showSection('data') && (onDownloadData || onImportData || onDeleteData) && (
          <div>
            <h3 className="text-sm font-medium text-[var(--text-secondary)] mb-3">
              Data Management
            </h3>
            <div className="space-y-3">
              {/* Download & Import side by side */}
              {(onDownloadData || onImportData) && (
                <div className="flex gap-2">
                  {onDownloadData && (
                    <button
                      type="button"
                      onClick={onDownloadData}
                      className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download
                    </button>
                  )}
                  {onImportData && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Import
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Import error */}
              {importError && (
                <p className="text-sm text-[var(--bac-danger)]">{importError}</p>
              )}

              {/* Import preview/confirmation */}
              {importPreview && onImportData && (
                <div className="p-4 bg-[var(--accent-orange)]/10 border border-[var(--accent-orange)]/30 rounded-lg space-y-3">
                  <p className="text-sm text-[var(--accent-orange)] font-medium">
                    This will replace all your current data.
                  </p>
                  <div className="text-sm text-[var(--text-secondary)]">
                    <p>File contains: <strong className="text-[var(--text-primary)]">{importPreview.entryCount} entries</strong> and <strong className="text-[var(--text-primary)]">{importPreview.drinkCount} drink templates</strong></p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setImportPreview(null)
                        setImportError('')
                      }}
                      className="btn btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onImportData(importPreview.data)
                        setImportPreview(null)
                        onClose()
                      }}
                      className="btn btn-primary flex-1"
                    >
                      Import Data
                    </button>
                  </div>
                </div>
              )}

              {onDeleteData && !showDeleteConfirm && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn w-full bg-[var(--bac-danger)]/10 border border-[var(--bac-danger)]/30 text-[var(--bac-danger)] hover:bg-[var(--bac-danger)]/20 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete All Data
                </button>
              )}

              {onDeleteData && showDeleteConfirm && (
                <div className="p-4 bg-[var(--bac-danger)]/10 border border-[var(--bac-danger)]/30 rounded-lg space-y-3">
                  <p className="text-sm text-[var(--bac-danger)] font-medium">
                    This will permanently delete all your drink entries, settings, and backups. This cannot be undone.
                  </p>
                  <div>
                    <label className="block text-xs text-[var(--text-secondary)] mb-1">
                      Type DELETE to confirm
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="DELETE"
                      className="w-full"
                      autoComplete="off"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteConfirm(false)
                        setDeleteConfirmText('')
                      }}
                      className="btn btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onDeleteData()
                        onClose()
                      }}
                      disabled={deleteConfirmText !== 'DELETE'}
                      className="btn flex-1 bg-[var(--bac-danger)] text-white hover:bg-[var(--bac-danger)]/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Delete Everything
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        </fieldset>
        {submitError && (
          <p className="text-sm text-[var(--bac-danger)] bg-[var(--bac-danger)]/10 border border-[var(--bac-danger)]/30 rounded-lg p-3">
            {submitError}
          </p>
        )}
      </form>
    </Modal>
  )
}
